import { useMemo } from "react";
import { type UserStory } from "@/lib/projectStore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, Target } from "lucide-react";
import BurndownChart from "./BurndownChart";
import CumulativeFlowDiagram from "./CumulativeFlowDiagram";

interface Props {
  stories: UserStory[];
  projectStartDate: string;
  sprintLengthWeeks: number;
}

export default function SprintVelocityChart({ stories, projectStartDate, sprintLengthWeeks }: Props) {
  const { sprintData, avgVelocity, trend } = useMemo(() => {
    const startMs = new Date(projectStartDate).getTime();
    const sprintMs = sprintLengthWeeks * 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const totalSprints = Math.max(1, Math.ceil((now - startMs) / sprintMs));
    const sprintCount = Math.min(totalSprints, 12); // show max 12 sprints

    const data: { sprint: string; points: number; planned: number; sprintIndex: number }[] = [];

    for (let i = 0; i < sprintCount; i++) {
      const sprintStart = startMs + i * sprintMs;
      const sprintEnd = sprintStart + sprintMs;

      // Count done stories whose dueDate (or updatedAt fallback) falls in this sprint
      let completed = 0;
      let planned = 0;

      stories.forEach((s) => {
        const storyStart = s.startDate ? new Date(s.startDate).getTime() : null;
        const storyEnd = s.dueDate ? new Date(s.dueDate).getTime() : null;
        const pts = s.storyPoints || 0;

        // Planned: story was scheduled to end in this sprint
        if (storyEnd && storyEnd >= sprintStart && storyEnd < sprintEnd) {
          planned += pts;
        }

        // Completed: done stories that were due/started in this sprint window
        if (s.status === "done" && storyEnd && storyEnd >= sprintStart && storyEnd < sprintEnd) {
          completed += pts;
        }
      });

      data.push({
        sprint: `S${i + 1}`,
        points: completed,
        planned,
        sprintIndex: i,
      });
    }

    const pointsArr = data.map((d) => d.points);
    const avg = pointsArr.length ? pointsArr.reduce((a, b) => a + b, 0) / pointsArr.length : 0;

    // Trend: compare last 3 sprints avg vs previous 3
    let trendDir: "up" | "down" | "flat" = "flat";
    if (data.length >= 4) {
      const recent = data.slice(-3).reduce((a, b) => a + b.points, 0) / 3;
      const earlier = data.slice(-6, -3).reduce((a, b) => a + b.points, 0) / Math.min(3, data.slice(-6, -3).length || 1);
      if (recent > earlier * 1.1) trendDir = "up";
      else if (recent < earlier * 0.9) trendDir = "down";
    }

    return { sprintData: data, avgVelocity: avg, trend: trendDir };
  }, [stories, projectStartDate, sprintLengthWeeks]);

  const totalDone = stories.filter((s) => s.status === "done").reduce((a, s) => a + (s.storyPoints || 0), 0);
  const totalPoints = stories.reduce((a, s) => a + (s.storyPoints || 0), 0);
  const hasData = sprintData.some((d) => d.points > 0);

  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor = trend === "up" ? "text-chart-2" : trend === "down" ? "text-destructive" : "text-muted-foreground";

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-5xl">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Avg Velocity</p>
            <p className="text-2xl font-bold font-display mt-1">{avgVelocity.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground">pts / sprint</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-bold font-display mt-1">{totalDone}</p>
            <p className="text-xs text-muted-foreground">of {totalPoints} pts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Trend</p>
            <div className="flex items-center gap-2 mt-1">
              <TrendIcon className={`h-5 w-5 ${trendColor}`} />
              <span className={`text-lg font-bold font-display ${trendColor}`}>
                {trend === "up" ? "Improving" : trend === "down" ? "Declining" : "Stable"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">last 3 sprints</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Remaining</p>
            <p className="text-2xl font-bold font-display mt-1">
              {avgVelocity > 0 ? Math.ceil((totalPoints - totalDone) / avgVelocity) : "—"}
            </p>
            <p className="text-xs text-muted-foreground">sprints to finish</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-display flex items-center gap-2">
            <Target className="h-4 w-4 text-accent" />
            Sprint Velocity
          </CardTitle>
          <CardDescription className="text-xs">
            Story points completed per sprint ({sprintLengthWeeks}w sprints)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!hasData ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              <div className="text-center space-y-2">
                <Target className="h-8 w-8 mx-auto opacity-40" />
                <p>No completed stories yet.</p>
                <p className="text-xs">Mark stories as done with due dates to see velocity data.</p>
              </div>
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sprintData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                  <XAxis
                    dataKey="sprint"
                    tick={{ fontSize: 11 }}
                    className="fill-muted-foreground"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    className="fill-muted-foreground"
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(value: number, name: string) => [
                      `${value} pts`,
                      name === "points" ? "Completed" : "Planned",
                    ]}
                  />
                  <ReferenceLine
                    y={avgVelocity}
                    stroke="hsl(var(--accent))"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                      value: `Avg: ${avgVelocity.toFixed(1)}`,
                      position: "right",
                      fontSize: 10,
                      fill: "hsl(var(--accent))",
                    }}
                  />
                  <Bar dataKey="points" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {sprintData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={
                          entry.points >= avgVelocity
                            ? "hsl(var(--chart-2))"
                            : "hsl(var(--chart-1))"
                        }
                        opacity={entry.points > 0 ? 1 : 0.3}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Burndown */}
      <BurndownChart
        stories={stories}
        projectStartDate={projectStartDate}
        sprintLengthWeeks={sprintLengthWeeks}
      />
      {/* Cumulative Flow */}
      <CumulativeFlowDiagram
        stories={stories}
        projectStartDate={projectStartDate}
        sprintLengthWeeks={sprintLengthWeeks}
      />
    </div>
  );
}
